import { useEffect, useMemo, useState } from 'react';
import {
  GridColDef,
  GridRenderCellParams,
  gridClasses,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  GridRowClassNameParams,
  GridValueGetter,
} from '@mui/x-data-grid-premium';
import { styled, useTheme } from '@mui/material/styles';
import RawTableContainer from '@mui/material/TableContainer';
import {
  ContractLegalStatus,
  ContractsByMissionGQL,
  TemplateContract,
  LegalContractGQL,
  MissionDataForGetLegalContractsResult,
} from '@bloomays-lib/types.shared';
import { FileViewer, LoaderPending, Tag, Modal } from '@bloomays-lib/ui.shared';
import RawDataGrid from '../molecules/DataGrid';
import IconButton from '@mui/material/IconButton';
import isEmpty from 'lodash/fp/isEmpty';
import values from 'lodash/fp/values';
import map from 'lodash/fp/map';
import compact from 'lodash/fp/compact';
import pipe from 'lodash/fp/pipe';
import flatten from 'lodash/fp/flatten';
import { useTranslation } from 'react-i18next';
import LegalContractFile from '../molecules/LegalContractFile';
import { useAuthStore } from '../../stores';

type TagContractTypeProps = {
  type: TemplateContract;
};

export type LegalContractRowData = LegalContractGQL &
  Omit<MissionDataForGetLegalContractsResult, 'id' | 'label'> & {
    missionId: string;
    missionLabel: string;
  };

const parseContractsByMissionGQL = (missionsContracts: ContractsByMissionGQL[]): LegalContractRowData[] => {
  const temp = map((contractByMission) => {
    const { mission, contracts } = contractByMission;
    const { bloomerId, bloomerFullName, clientSocietyId, clientSocietyName, bloomerSocietyName } = mission;

    const rows: LegalContractRowData[] = map((contract) => {
      return {
        ...contract,
        bloomerId,
        bloomerFullName,
        clientSocietyId,
        clientSocietyName,
        bloomerSocietyName,
        missionId: mission.id,
        missionLabel: mission.label,
      };
    }, contracts);
    return rows;
  }, missionsContracts);

  return pipe(flatten, compact)(temp);
};

const TagContractType = ({ type }: TagContractTypeProps) => {
  const theme = useTheme();
  return (
    <Tag
      text={type}
      aria-label={type}
      backgroundColor={type === TemplateContract.SST ? theme.palette.blue[60] : theme.palette.red[60]}
      color={theme.palette.common.white}
    />
  ) as React.ReactNode;
};

export type TableLegalContracts = {
  missionsContracts: ContractsByMissionGQL[];
};

const TableLegalContracts = ({ missionsContracts }: TableLegalContracts) => {
  const { t } = useTranslation(['legalContracts', 'mission']);
  const apiRef = useGridApiRef();
  const [token, setToken] = useState<string | undefined>();
  const auth = useAuthStore((state) => state.auth);

  useEffect(() => {
    if (auth?.getAccessTokenSilently) {
      auth.getAccessTokenSilently().then((token) => {
        setToken(token);
      });
    }
  }, [auth]);

  const [fileViewerPath, setFileViewerPath] = useState<string | undefined>(undefined);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  const legalContracts: LegalContractRowData[] = useMemo(
    () => parseContractsByMissionGQL(missionsContracts),
    [missionsContracts],
  );

  const groupedByMission = missionsContracts.length !== 1;
  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      sorting: {
        sortModel: [{ field: 'createdAt', sort: 'asc' }],
      },
      rowGrouping: groupedByMission
        ? {
            model: ['missionLabel'],
          }
        : undefined,
    },
  });

  const contractStatus: Record<ContractLegalStatus, { value: ContractLegalStatus; label: string }> = {
    'on going': { value: 'on going', label: t('onGoing') },
    done: { value: 'done', label: t('done') },
  };
  const columns: GridColDef<LegalContractRowData>[] = [
    {
      field: 'step',
      headerName: t('step'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    },
    {
      field: 'type',
      headerName: t('type'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'singleSelect',
      valueOptions: [TemplateContract.SST, TemplateContract.PDS],
      renderCell: (params) => {
        if (params.rowNode.type === 'group') {
          if (params.field !== '__row_group_by_columns_group__') return '';
          const groupingKey = params.rowNode.groupingKey as TemplateContract;
          if (!groupingKey) return;
          return (<TagContractType type={groupingKey} />) as React.ReactNode;
        }

        if (params.row.type) {
          return (<TagContractType type={params.row.type} />) as React.ReactNode;
        }
      },
    },
    {
      field: 'status',
      headerName: t('status'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'singleSelect',
      valueOptions: values(contractStatus),
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        if (params.rowNode.type === 'group') {
          if (params.field !== '__row_group_by_columns_group__') return '';
          const groupingKey = params.rowNode.groupingKey as ContractLegalStatus;
          if (!groupingKey) return;
          const t = contractStatus[groupingKey];
          return (
            <IconButton size="small" title={t.label}>
              {t.label}
            </IconButton>
          ) as React.ReactNode;
        }

        if (params.row.status) {
          const t = contractStatus[params.row.status];
          return (
            <IconButton size="small" title={t.label}>
              {t.label}
            </IconButton>
          ) as React.ReactNode;
        }
      },
    },
    {
      field: 'createdAt',
      headerName: t('createdAt'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'date',
      valueGetter: (value: GridValueGetter<LegalContractRowData>, row: LegalContractRowData): Date | undefined => {
        return row.createdAt ? new Date(row.createdAt) : undefined;
      },
    },
    {
      field: 'signedDate',
      headerName: t('signedDate'),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      type: 'date',
      valueGetter: (value: GridValueGetter<LegalContractRowData>, row: LegalContractRowData): Date | undefined => {
        return row.signedDate ? new Date(row.signedDate) : undefined;
      },
    },
    {
      field: 'emptyFileUrl',
      headerName: t('emptyFileUrl'),
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        if (params.rowNode.type === 'group') {
          return '';
        }
        return (
          <LegalContractFile
            setUploadingFile={setUploadingFile}
            legalContract={params.row}
            signed={false}
            missionRecordId={params.row.missionId}
            createInvitation={true}
            onClickPreview={setFileViewerPath}
          />
        ) as React.ReactNode;
      },
    },
    {
      field: 'signedFileUrl',
      headerName: t('signedFileUrl'),
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      renderCell: (params: GridRenderCellParams<LegalContractRowData>) => {
        if (params.rowNode.type === 'group') {
          return '';
        }
        return (
          <LegalContractFile
            setUploadingFile={setUploadingFile}
            legalContract={params.row}
            signed={true}
            missionRecordId={params.row.missionId}
            createInvitation={false}
            onClickPreview={setFileViewerPath}
          />
        ) as React.ReactNode;
      },
    },
  ];

  if (groupedByMission) {
    columns.unshift({
      field: 'clientSocietyName',
      headerName: t('clientSocietyName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
    columns.unshift({
      field: 'bloomerSocietyName',
      headerName: t('bloomerSocietyName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
    columns.unshift({
      field: 'bloomerFullName',
      headerName: t('bloomerFullName', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
    columns.unshift({
      field: 'missionLabel',
      headerName: t('label', { ns: 'mission' }),
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
    });
  }

  return (
    <>
      {uploadingFile && <LoaderPending text={t('uploading')} />}
      {!uploadingFile && isEmpty(legalContracts) && <>{t('noContractFounds')}</>}
      <Modal
        open={!!fileViewerPath}
        onClose={() => setFileViewerPath(undefined)}
        additionnalCSS={{
          width: '1250px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
          height: '98%',
        }}
      >
        <FileViewer width="1200px" url={fileViewerPath as string} accessToken={token} mimeType="application/pdf" />
      </Modal>
      <TableContainer>
        <DataGrid
          apiRef={apiRef}
          uniqueDatagridId="table-contracts-v1"
          initialState={initialState}
          getRowClassName={(params: GridRowClassNameParams) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          hideFooterPagination={false}
          sortingMode="client"
          filterMode="client"
          rows={legalContracts}
          getRowId={(row: LegalContractGQL) => row.recordId as string}
          columns={columns}
          pageSize={20}
          pagination={true}
          sx={{
            width: '100%',
          }}
        />
      </TableContainer>
    </>
  ) as React.ReactNode;
};

export default TableLegalContracts;

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));

const TableContainer = styled(RawTableContainer)(() => ({
  margin: '0 auto',
}));
